import { useState } from "react";

import { ApiError } from "@/lib/api";
import { usePaymentMethods, useRemovePayment, useSetDefaultPayment } from "../api";

// Phase 4a customer-portal FR-9 + FR-10. Lists active payment
// sources (brand + ••••last4 + exp + default), lets the customer
// flip default + remove. Removing the only card backing an active
// rental returns 422; we surface the server's message inline.
//
// Never displays the PAN — only the gateway tokens (brand, last4,
// exp_month, exp_year, default flag).
export function PaymentMethodsTab(): JSX.Element {
  const q = usePaymentMethods();
  const setDefault = useSetDefaultPayment();
  const remove = useRemovePayment();
  const [removeError, setRemoveError] = useState<string | null>(null);

  if (q.isLoading) return <div className="text-sm text-zinc-600">Loading…</div>;
  if (!q.data) return <div className="text-sm text-red-700">Couldn&rsquo;t load payment methods.</div>;

  function handleRemove(id: number): void {
    setRemoveError(null);
    remove.mutate(id, {
      onError: (e) => {
        const msg = e instanceof ApiError ? readErrorMessage(e) : e.message;
        setRemoveError(msg);
      }
    });
  }

  return (
    <div className="space-y-2 text-sm">
      {q.data.rows.length === 0 ? (
        <p className="text-xs text-zinc-500">No saved cards.</p>
      ) : (
        <ul className="space-y-2">
          {q.data.rows.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between rounded border border-zinc-200 p-2"
              data-testid={`payment-${p.id}`}
            >
              <div>
                <div className="font-medium">
                  {p.brand} •••• {p.last4}
                </div>
                <div className="text-xs text-zinc-500">
                  Exp {String(p.exp_month).padStart(2, "0")}/{String(p.exp_year).slice(-2)}
                </div>
                {p.default ? <span className="text-xs text-green-700">default</span> : null}
              </div>
              <div className="flex gap-2">
                {!p.default ? (
                  <button
                    type="button"
                    onClick={() => setDefault.mutate(p.id)}
                    disabled={setDefault.isPending || remove.isPending}
                    className="text-xs underline disabled:opacity-40"
                    data-testid={`make-default-${p.id}`}
                  >
                    Make default
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleRemove(p.id)}
                  disabled={setDefault.isPending || remove.isPending}
                  className="text-xs underline disabled:opacity-40"
                  data-testid={`remove-${p.id}`}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {removeError ? (
        <div className="rounded border border-red-600 bg-red-50 p-2 text-sm" data-testid="remove-error">
          {removeError}
        </div>
      ) : null}

      <p className="pt-2 text-xs text-zinc-500">
        To add a card, place a new reservation — JDR saves the card on file at checkout.
      </p>
    </div>
  );
}

function readErrorMessage(e: ApiError): string {
  const body = e.body as { message?: string } | null;
  return body?.message ?? "Couldn't remove this card.";
}
